/**
 * Logic Gates Configuration & Monitor
 *
 * Shows the 5 signal gates with live pass/fail stats from the last 24 hours.
 * Gate thresholds are adjustable — changes are written to system_config and
 * picked up by DataCollector at the next full rotation cycle (~4 min).
 */

import React, { useState, useEffect, useCallback } from 'react'
import { RefreshCw, ShieldCheck, AlertTriangle, TrendingUp, Activity, Sliders } from 'lucide-react'
import { api, gates as gatesApi } from '../lib/api'

const mono = { fontFamily: 'JetBrains Mono, monospace' }

const darkInput = {
  background: '#0f172a', border: '1px solid #374151',
  color: '#e2e8f0', borderRadius: '0.5rem',
  padding: '0.4rem 0.75rem', outline: 'none',
  ...mono, fontSize: '0.8rem', width: '90px', textAlign: 'right',
}

// Gate definitions — each gate has a label, key, description, and display config
const GATES = [
  {
    key:    'gate_min_confidence',
    label:  'Min Confidence',
    desc:   'Minimum signal confidence score (0–1). Sector bearish penalty (×0.80, see below) reduces BUY confidence before this check.',
    unit:   '',
    step:   0.01,
    min:    0,
    max:    1,
    statKey: 'confidence_ok',
    color:  '#6366f1',
    fmt:    v => v.toFixed(2),
  },
  {
    key:    'gate_min_volume_ratio',
    label:  'Min Volume Ratio',
    desc:   'Minimum volume relative to the 20-bar average. 0.70 = 70% of average volume required.',
    unit:   'x',
    step:   0.05,
    min:    0,
    max:    3,
    statKey: 'volume_ok',
    color:  '#22d3ee',
    fmt:    v => v.toFixed(2) + 'x',
  },
  {
    key:    'gate_max_atr_percent',
    label:  'Max ATR %',
    desc:   'Maximum ATR as % of price. Blocks entries when volatility is too high (erratic price action).',
    unit:   '%',
    step:   0.25,
    min:    0.5,
    max:    15,
    statKey: 'volatility_ok',
    color:  '#f59e0b',
    fmt:    v => v.toFixed(2) + '%',
  },
  {
    key:    'gate_min_strength',
    label:  'Min Strength',
    desc:   'Minimum signal strength score (0–1). Reflects how far the indicator is from its threshold.',
    unit:   '',
    step:   0.05,
    min:    0,
    max:    1,
    statKey: 'strength_ok',
    color:  '#10b981',
    fmt:    v => v.toFixed(2),
  },
  {
    key:    null,   // special — no DB key, evaluated via market_regime
    label:  'Regime Alignment',
    desc:   'BUY signals blocked when market_regime = BEAR. SELL signals always pass this gate.',
    unit:   '',
    step:   null,
    statKey: 'regime_ok',
    color:  '#a78bfa',
    fmt:    () => 'BUY blocked in BEAR',
    readOnly: true,
  },
  {
    key:    null,   // FIX 2026-07-22: was a fake-editable 'gate_sector_penalty' system_config
                    // key that sf_data_collector.py's own code comment confirms was "never
                    // read by evaluate_gates() or detect_signals()" — changing it here did
                    // nothing. The real mechanism (aka this system's "Sector Rotation" logic,
                    // in git history since 2026-03-03) is SECTOR_BEARISH_PENALTY, a hardcoded
                    // 0.80 constant in sf_data_collector.py applied when a symbol's sector
                    // ETF's DAILY SuperTrend direction is bearish (NOT EMA20/EMA50 — that was
                    // also wrong). Shown read-only from the backend's static info value.
    label:  'Sector Bearish Penalty',
    desc:   'Confidence multiplier on BUY signals when the symbol\'s sector ETF DAILY SuperTrend is bearish. Fixed in code (sf_data_collector.py::SECTOR_BEARISH_PENALTY) — not adjustable here.',
    unit:   '×',
    step:   null,
    statKey: null,
    color:  '#f43f5e',
    fmt:    v => `${Number(v).toFixed(2)}×`,
    readOnly: true,
    readOnlyKey: 'sector_bearish_penalty',
  },
  {
    // Added 2026-07-24. Genuinely live/editable -- reloaded from system_config
    // every scan cycle in sf_signal_scanner.py's run_scan() (unlike the sector
    // penalty above). This is an execution-time SELL gate, not a signal-
    // generation gate like the first 4 cards -- it applies to
    // MFI_OVERBOUGHT_CROSS/STOCH_OVERBOUGHT_CROSS SELL signals specifically,
    // which get no loss-bypass: below this floor they simply don't fire.
    key:    'min_profit_to_sell',
    label:  'Min Profit to Sell',
    desc:   'Minimum unrealized gain required before a SELL signal (overbought cross) is allowed to execute. No loss-bypass for this gate — these signal types assume a profitable position.',
    unit:   '%',
    step:   0.1,
    min:    0,
    max:    5,
    statKey: null,
    color:  '#22c55e',
    // stored as a decimal (0.008 = 0.8%) — display/edit in percent.
    // fmt receives the already-converted display-scale value (via
    // effectiveVal -> toDisplay), so it must NOT multiply by 100 again.
    toDisplay: v => v * 100,
    toStorage: v => v / 100,
    fmt:    v => `${Number(v).toFixed(2)}%`,
  },
  {
    key:    null,   // RED-tier dynamic override — hardcoded literal in
                    // sf_signal_scanner.py's RED-tier branch, not itself a
                    // system_config key. Read-only info, same pattern as
                    // Sector Bearish Penalty above.
    label:  'Min Profit to Sell (RED tier)',
    desc:   'When buying power falls below the RED-tier floor, the sell floor above drops to this value for that cycle — takes smaller gains faster to recycle capital. Fixed in code, not adjustable here.',
    unit:   '%',
    step:   null,
    statKey: null,
    color:  '#facc15',
    fmt:    v => `${(Number(v) * 100).toFixed(2)}%`,
    readOnly: true,
    readOnlyKey: 'min_profit_to_sell_red_tier',
  },
]

function pct(num, denom) {
  if (!denom || denom === 0) return null
  return Math.round((num / denom) * 100)
}

function GateBar({ pass, total, color }) {
  const p = pct(pass, total)
  if (p === null) return <span className="text-slate-700" style={{ ...mono, fontSize: '0.65rem' }}>—</span>
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 rounded-full h-1.5" style={{ background: '#1f2937' }}>
        <div className="h-1.5 rounded-full transition-all" style={{ width: `${p}%`, background: color }} />
      </div>
      <span className="tabular-nums w-10 text-right" style={{ color, ...mono, fontSize: '0.7rem' }}>{p}%</span>
    </div>
  )
}

export default function LogicGates() {
  const [thresholds, setThresholds] = useState({})
  const [stats,      setStats]      = useState({})
  const [edits,      setEdits]      = useState({})   // pending unsaved edits
  const [loading,    setLoading]    = useState(true)
  const [saving,     setSaving]     = useState(false)
  const [error,      setError]      = useState(null)
  const [saveMsg,    setSaveMsg]    = useState(null)

  const load = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const data = await gatesApi.get()
      if (!data.success) throw new Error(data.error || 'Request failed')
      setThresholds(data.thresholds || {})
      setStats(data.stats_24h || {})
      setEdits({})
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Most gates are edited/displayed in their native stored scale (0-1 for
  // confidence/strength, etc). min_profit_to_sell is stored as a tiny decimal
  // (0.008 = 0.8%) -- edited/displayed in percent via toDisplay/toStorage.
  const gateByKey = Object.fromEntries(GATES.filter(g => g.key).map(g => [g.key, g]))
  const toDisplayVal = (key, v) => gateByKey[key]?.toDisplay ? gateByKey[key].toDisplay(v) : v
  const toStorageVal = (key, v) => gateByKey[key]?.toStorage ? gateByKey[key].toStorage(v) : v

  const effectiveVal = (key) =>
    edits[key] !== undefined ? edits[key] : (thresholds[key] != null ? toDisplayVal(key, thresholds[key]) : '')

  const handleEdit = (key, raw) => {
    const v = parseFloat(raw)
    if (!isNaN(v)) setEdits(prev => ({ ...prev, [key]: v }))
    else setEdits(prev => ({ ...prev, [key]: raw }))
  }

  const hasChanges = Object.keys(edits).length > 0

  const handleSave = async () => {
    const payload = {}
    for (const [k, v] of Object.entries(edits)) {
      const fv = parseFloat(v)
      if (isNaN(fv)) { setError(`Invalid value for ${k}`); return }
      payload[k] = toStorageVal(k, fv)
    }
    setSaving(true); setError(null); setSaveMsg(null)
    try {
      const data = await gatesApi.save(payload)
      if (!data.success) throw new Error(data.error || 'Save failed')
      setSaveMsg('Saved — engine picks up changes at next full rotation cycle (~5 min)')
      await load()
    } catch (e) {
      setError(e.message)
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => { setEdits({}); setError(null); setSaveMsg(null) }

  const total = stats.total || 0
  const actionable = stats.actionable || 0

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-slate-200 font-semibold" style={{ fontFamily: 'Rajdhani, sans-serif', letterSpacing: '0.1em', fontSize: '1rem' }}>
            LOGIC GATES
          </h2>
          <p className="text-slate-600 mt-0.5" style={{ ...mono, fontSize: '0.65rem' }}>
            First 5 cards: BUY signal-generation gates — all must pass for a signal to be actionable.
            Remaining cards: SELL-side execution gate + informational values, not part of the BUY pass/fail.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <>
              <button onClick={handleReset}
                className="px-3 py-1.5 rounded-lg text-xs font-medium"
                style={{ background: '#1f2937', color: '#94a3b8', border: '1px solid #374151', ...mono, fontSize: '0.68rem' }}>
                RESET
              </button>
              <button onClick={handleSave} disabled={saving}
                className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-medium disabled:opacity-50"
                style={{ background: '#15803d', color: '#bbf7d0', border: '1px solid rgba(34,197,94,0.35)', ...mono, fontSize: '0.68rem' }}>
                {saving ? <RefreshCw className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
                SAVE CHANGES
              </button>
            </>
          )}
          <button onClick={load} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-40"
            style={{ background: '#111827', color: '#64748b', border: '1px solid #1f2937', ...mono, fontSize: '0.68rem' }}>
            <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} /> REFRESH
          </button>
        </div>
      </div>

      {/* Save feedback */}
      {saveMsg && (
        <div className="rounded-lg px-4 py-3" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)' }}>
          <p className="text-emerald-400" style={{ ...mono, fontSize: '0.7rem' }}>{saveMsg}</p>
        </div>
      )}
      {error && (
        <div className="rounded-lg px-4 py-3" style={{ background: 'rgba(244,63,94,0.08)', border: '1px solid rgba(244,63,94,0.25)' }}>
          <p className="text-rose-400" style={{ ...mono, fontSize: '0.7rem' }}>{error}</p>
        </div>
      )}

      {/* 24-hour summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Signals (24h)',  value: total,      color: '#6366f1' },
          { label: 'Actionable',     value: actionable, color: '#10b981', sub: total > 0 ? `${pct(actionable, total)}% of total` : null },
          { label: 'BUY signals',    value: stats.buy_count  || 0, color: '#22d3ee' },
          { label: 'SELL signals',   value: stats.sell_count || 0, color: '#f59e0b' },
        ].map(({ label, value, color, sub }) => (
          <div key={label} className="rounded-xl p-4"
            style={{ background: '#111827', border: '1px solid #1f2937', borderTop: `1.5px solid ${color}`, boxShadow: `0 -1px 8px ${color}33` }}>
            <p className="text-slate-600 uppercase" style={{ fontSize: '0.6rem', letterSpacing: '0.12em', fontWeight: 600 }}>{label}</p>
            <p className="font-bold mt-1.5 tabular-nums" style={{ color, ...mono, fontSize: '1.4rem' }}>
              {loading ? '—' : value}
            </p>
            {sub && <p className="text-slate-600 mt-0.5" style={{ ...mono, fontSize: '0.62rem' }}>{sub}</p>}
          </div>
        ))}
      </div>

      {/* Gate cards */}
      <div className="space-y-3">
        <p className="text-slate-600 uppercase" style={{ ...mono, fontSize: '0.6rem', letterSpacing: '0.1em' }}>
          Gate Configuration &amp; 24-Hour Pass Rates
        </p>

        {GATES.map((gate, i) => {
          const passCount = gate.statKey ? (stats[gate.statKey] || 0) : null
          const passPct   = gate.statKey ? pct(passCount, total) : null

          return (
            <div key={gate.key || gate.label} className="rounded-xl p-5"
              style={{ background: '#111827', border: '1px solid #1f2937',
                       borderLeft: `3px solid ${gate.color}` }}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">

                {/* Gate number + label */}
                <div className="flex items-center gap-3 min-w-[180px]">
                  <div className="flex items-center justify-center w-7 h-7 rounded-full flex-shrink-0"
                    style={{ background: `${gate.color}18`, border: `1px solid ${gate.color}55` }}>
                    <span style={{ color: gate.color, ...mono, fontSize: '0.7rem', fontWeight: 700 }}>{i + 1}</span>
                  </div>
                  <div>
                    <p className="text-slate-300 font-semibold" style={{ ...mono, fontSize: '0.78rem', letterSpacing: '0.04em' }}>
                      {gate.label}
                    </p>
                    <p className="text-slate-600 mt-0.5" style={{ fontSize: '0.62rem', maxWidth: '340px' }}>
                      {gate.desc}
                    </p>
                  </div>
                </div>

                {/* Pass rate bar */}
                <div className="flex-1 min-w-[120px]">
                  {loading ? (
                    <div className="h-1.5 w-full rounded-full" style={{ background: '#1f2937' }} />
                  ) : (
                    <GateBar pass={passCount} total={total} color={gate.color} />
                  )}
                  {passPct !== null && !loading && (
                    <p className="text-slate-600 mt-1" style={{ ...mono, fontSize: '0.6rem' }}>
                      {passCount.toFixed(0)} / {total} signals passed
                    </p>
                  )}
                </div>

                {/* Threshold value (editable or read-only) */}
                <div className="flex items-center gap-3 ml-auto">
                  {gate.readOnly ? (
                    <div className="rounded-lg px-3 py-2 text-center"
                      style={{ background: '#0f172a', border: '1px solid #1f2937', minWidth: '90px' }}>
                      <p style={{ color: gate.color, ...mono, fontSize: '0.68rem' }}>
                        {gate.readOnlyKey ? gate.fmt(thresholds[gate.readOnlyKey] ?? 0) : 'RULE BASED'}
                      </p>
                    </div>
                  ) : gate.key ? (
                    <div>
                      <p className="text-slate-700 mb-1 text-center" style={{ ...mono, fontSize: '0.58rem' }}>THRESHOLD</p>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => handleEdit(gate.key, Math.max(gate.min, parseFloat(effectiveVal(gate.key)) - gate.step))}
                          className="w-6 h-6 flex items-center justify-center rounded"
                          style={{ background: '#1f2937', color: '#64748b', fontSize: '0.9rem' }}>−</button>
                        <input
                          type="number"
                          step={gate.step}
                          min={gate.min}
                          max={gate.max}
                          value={effectiveVal(gate.key)}
                          onChange={e => handleEdit(gate.key, e.target.value)}
                          style={{
                            ...darkInput,
                            borderColor: edits[gate.key] !== undefined ? gate.color : '#374151',
                            boxShadow: edits[gate.key] !== undefined ? `0 0 0 1px ${gate.color}44` : 'none',
                          }}
                        />
                        <button
                          onClick={() => handleEdit(gate.key, Math.min(gate.max, parseFloat(effectiveVal(gate.key)) + gate.step))}
                          className="w-6 h-6 flex items-center justify-center rounded"
                          style={{ background: '#1f2937', color: '#64748b', fontSize: '0.9rem' }}>+</button>
                      </div>
                      <p className="text-slate-700 mt-1 text-center" style={{ ...mono, fontSize: '0.6rem' }}>
                        {typeof effectiveVal(gate.key) === 'number' ? gate.fmt(effectiveVal(gate.key)) : gate.fmt(thresholds[gate.key] ?? 0)}
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Actionable signal breakdown */}
      {(stats.actionable_breakdown?.length > 0) && (
        <div className="rounded-xl p-5" style={{ background: '#111827', border: '1px solid #1f2937' }}>
          <p className="text-slate-500 uppercase mb-4" style={{ ...mono, fontSize: '0.6rem', letterSpacing: '0.1em' }}>
            Actionable Signals (24h) — Breakdown
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {stats.actionable_breakdown.map(row => (
              <div key={`${row.type}-${row.direction}`} className="rounded-lg p-3"
                style={{ background: '#0f172a', border: '1px solid #1f2937' }}>
                <p style={{ color: row.direction === 'BUY' ? '#22d3ee' : '#f59e0b', ...mono, fontSize: '0.62rem', letterSpacing: '0.08em' }}>
                  {row.direction}
                </p>
                <p className="text-slate-300 font-medium mt-0.5" style={{ ...mono, fontSize: '0.72rem' }}>
                  {row.type}
                </p>
                <p style={{ color: '#10b981', ...mono, fontSize: '1.1rem', fontWeight: 700, marginTop: '0.25rem' }}>
                  {row.count}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Avg indicator values */}
      <div className="rounded-xl p-5" style={{ background: '#111827', border: '1px solid #1f2937' }}>
        <p className="text-slate-500 uppercase mb-4" style={{ ...mono, fontSize: '0.6rem', letterSpacing: '0.1em' }}>
          24-Hour Signal Averages
        </p>
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Avg Confidence',    value: stats.avg_confidence,   fmt: v => v.toFixed(3), color: '#6366f1' },
            { label: 'Avg Strength',      value: stats.avg_strength,     fmt: v => v.toFixed(3), color: '#10b981' },
            { label: 'Avg Volume Ratio',  value: stats.avg_volume_ratio, fmt: v => v.toFixed(2) + 'x', color: '#22d3ee' },
          ].map(({ label, value, fmt, color }) => (
            <div key={label}>
              <p className="text-slate-700 uppercase mb-1" style={{ ...mono, fontSize: '0.6rem', letterSpacing: '0.08em' }}>{label}</p>
              <p className="tabular-nums font-bold" style={{ color, ...mono, fontSize: '1.2rem' }}>
                {loading || !value ? '—' : fmt(value)}
              </p>
            </div>
          ))}
        </div>
      </div>

    </div>
  )
}
